import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";

import { makeStyles } from "@mui/styles";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import {
  createPlacesSessionToken,
  fetchPlaceAutocomplete,
  fetchPlaceDetails,
} from "apis/placesAPIs";

const DEBOUNCE_MS = 250;
const COUNTRY_RESTRICTIONS = ["au", "my", "nz", "sg"];

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    position: "sticky",
    top: 0,
    marginBottom: theme.spacing(2),
    zIndex: 999,
  },
}));

const getComponent = (components, type) => {
  for (let i = 0; i < components.length; i += 1) {
    if (components[i].types.includes(type)) {
      return components[i].long_name;
    }
  }
  return "";
};

const getShortComponent = (components, type) => {
  for (let i = 0; i < components.length; i += 1) {
    if (components[i].types.includes(type)) {
      return components[i].short_name;
    }
  }
  return "";
};

const buildAddressFromComponents = (components, geometry) => ({
  country: getComponent(components, "country"),
  countryCode: getShortComponent(components, "country"),
  line1: `${getComponent(components, "street_number")} ${getComponent(components, "route")}`.trim(),
  line2: getComponent(components, "sublocality_level_1"),
  line3: "",
  city: getComponent(components, "locality"),
  stateOrProvince: getComponent(components, "administrative_area_level_1"),
  stateOrProvinceCode: getShortComponent(components, "administrative_area_level_1"),
  postalCode: getComponent(components, "postal_code"),
  location: geometry && geometry.location ? geometry.location : null,
});

const SingleAddressField = ({ addressObject, onAddressChange }) => {
  const classes = useStyles();

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [useManualInput, setUseManualInput] = useState(false);
  const [manualAddress, setManualAddress] = useState("");

  const sessionTokenRef = useRef(createPlacesSessionToken());
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!addressObject) return;
    if (addressObject.line1) {
      setManualAddress(addressObject.line1);
    }
  }, [addressObject]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  const runSearch = useCallback(async (query) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const predictions = await fetchPlaceAutocomplete(
        {
          input: query,
          sessionToken: sessionTokenRef.current,
          countries: COUNTRY_RESTRICTIONS,
          language: "en",
        },
        { signal: controller.signal }
      );
      if (!controller.signal.aborted) {
        setOptions(predictions);
      }
    } catch (error) {
      if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
        console.error("Place autocomplete failed", error);
        setOptions([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!inputValue || !inputValue.trim()) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      runSearch(inputValue);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, runSearch]);

  const handleSelect = async (_event, prediction) => {
    setSelected(prediction);
    if (!prediction || !prediction.placeId) return;

    try {
      const { addressComponents, geometry } = await fetchPlaceDetails({
        placeId: prediction.placeId,
        sessionToken: sessionTokenRef.current,
        language: "en",
      });

      sessionTokenRef.current = createPlacesSessionToken();

      if (!addressComponents || addressComponents.length === 0) return;

      const newAddress = buildAddressFromComponents(
        addressComponents,
        geometry
      );
      onAddressChange(newAddress);
    } catch (error) {
      console.error("Place details failed", error);
    }
  };

  const handleManualAddressChange = (event) => {
    const value = event.target.value;
    setManualAddress(value);
    onAddressChange({
      country: "",
      line1: value,
      line2: "",
      line3: "",
      city: "",
      stateOrProvince: "",
      postalCode: "",
    });
  };

  const noOptionsText = useMemo(
    () => (inputValue.trim() ? "No matches" : "Start typing your address"),
    [inputValue]
  );

  return (
    <div>
      <div className={classes.autocomplete}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography gutterBottom>Enter your address:</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setUseManualInput((v) => !v)}
          >
            {useManualInput ? "Use Autocomplete" : "Manual Entry"}
          </Button>
        </Box>

        {useManualInput ? (
          <TextField
            fullWidth
            label="Enter your address"
            placeholder="e.g., 123 Main Street, Sydney NSW 2000"
            value={manualAddress}
            onChange={handleManualAddressChange}
            variant="outlined"
            margin="dense"
            helperText="Manual address entry"
          />
        ) : (
          <Autocomplete
            fullWidth
            filterOptions={(x) => x}
            options={options}
            value={selected}
            inputValue={inputValue}
            loading={loading}
            noOptionsText={noOptionsText}
            getOptionLabel={(option) =>
              (option && option.description) || ""
            }
            isOptionEqualToValue={(option, value) =>
              option.placeId === value.placeId
            }
            onInputChange={(_event, value) => setInputValue(value)}
            onChange={handleSelect}
            renderOption={(props, option) => (
              <li {...props} key={option.placeId}>
                <div>
                  <div>{option.mainText || option.description}</div>
                  {option.secondaryText ? (
                    <Typography variant="caption" color="textSecondary">
                      {option.secondaryText}
                    </Typography>
                  ) : null}
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Start typing your address"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

export default SingleAddressField;
